import { Arg, Args, Ctx, Directive, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { Attachment } from '@things-factory/attachment-base'
import { Role, User } from '@things-factory/auth-base'
import { Board } from '@things-factory/board-service'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam, ScalarDate } from '@things-factory/shell'

import { DataKeySet } from '../data-key-set/data-key-set.js'
import { DataSet } from '../data-set/data-set.js'
import { DataSetHistory } from './data-set-history.js'
import { DataSetHistoryList } from './data-set-history-type.js'

@Resolver(DataSetHistory)
export class DataSetHistoryQuery {
  @Directive('@privilege(category: "data-set-history", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSetHistory, { description: 'To fetch a DataSetHistory' })
  async dataSetHistory(
    @Arg('id') id: string,
    @Arg('timestamp', type => ScalarDate) timestamp: Date,
    @Ctx() context: ResolverContext
  ): Promise<DataSetHistory> {
    const { domain } = context.state

    return await getRepository(DataSetHistory).findOne({
      where: { domain: { id: domain.id }, originalId: id, updatedAt: timestamp }
    })
  }

  @Directive('@privilege(category: "data-set-history", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataSetHistoryList, { description: 'To fetch multiple DataSetHistories' })
  async dataSetHistories(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSetHistoryList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSetHistory),
      params,
      domain,
      searchables: ['name', 'description', 'supervisoryRole']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => Role)
  async entryRole(@Root() dataSetHistory: DataSetHistory): Promise<Role> {
    return (
      dataSetHistory.entryRoleId &&
      (await getRepository(Role).findOneBy({
        id: dataSetHistory.entryRoleId
      }))
    )
  }

  @FieldResolver(type => Role)
  async supervisoryRole(@Root() dataSetHistory: DataSetHistory): Promise<Role> {
    return (
      dataSetHistory.supervisoryRoleId &&
      (await getRepository(Role).findOneBy({
        id: dataSetHistory.supervisoryRoleId
      }))
    )
  }

  @FieldResolver(type => Board, { nullable: true })
  async entryBoard(@Root() dataSetHistory: DataSetHistory) {
    if (dataSetHistory.entryType == 'board' && dataSetHistory.entryView) {
      return await getRepository(Board).findOneBy({ id: dataSetHistory.entryView })
    }
  }

  @FieldResolver(type => Board, { nullable: true })
  async monitorBoard(@Root() dataSetHistory: DataSetHistory) {
    if (dataSetHistory.monitorType == 'board' && dataSetHistory.monitorView) {
      return await getRepository(Board).findOneBy({ id: dataSetHistory.monitorView })
    }
  }

  @FieldResolver(type => DataKeySet, { nullable: true })
  async dataKeySet(@Root() dataSet: DataSet) {
    return (
      dataSet.dataKeySet ||
      (dataSet.dataKeySetId &&
        (await getRepository(DataKeySet).findOneBy({
          id: dataSet.dataKeySetId
        })))
    )
  }

  @FieldResolver(type => User)
  async timestamp(@Root() dataSetHistory: DataSetHistory): Promise<Date> {
    return dataSetHistory.updatedAt
  }

  @FieldResolver(type => Domain)
  async domain(@Root() dataSetHistory: DataSetHistory): Promise<Domain> {
    return dataSetHistory.domainId && (await getRepository(Domain).findOneBy({ id: dataSetHistory.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() dataSetHistory: DataSetHistory): Promise<User> {
    return dataSetHistory.updaterId && (await getRepository(User).findOneBy({ id: dataSetHistory.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() dataSetHistory: DataSetHistory): Promise<User> {
    return dataSetHistory.creatorId && (await getRepository(User).findOneBy({ id: dataSetHistory.creatorId }))
  }

  @FieldResolver(type => String)
  async reportTemplate(@Root() dataSetHistory: DataSetHistory): Promise<string | undefined> {
    const attachment: Attachment = await getRepository(Attachment).findOne({
      where: {
        domain: { id: dataSetHistory.domainId },
        refType: `${DataSet.name}-report-template`,
        refBy: dataSetHistory.originalId
      }
    })

    return attachment?.path
  }
}
